import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authApi } from '@/lib/api';

const AuthContext = createContext(null);
const SESSION_TIMEOUT_MS = 60 * 60 * 1000; // 60 minutes
const WARNING_BEFORE_MS = 2 * 60 * 1000; // warn 2 min before

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionWarning, setSessionWarning] = useState(false);
  const [remainingSeconds, setRemainingSeconds] = useState(0);
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);

  const fetchUser = useCallback(async () => {
    try {
      const res = await authApi.me();
      setUser(res.data);
    } catch {
      localStorage.removeItem('token');
      setUser(null);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      fetchUser().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [fetchUser]);

  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    if (sessionWarning) setSessionWarning(false);
  }, [sessionWarning]);

  // NO automatic activity tracking - timer only resets via extendSession() button or explicit call

  // Session timeout checker
  useEffect(() => {
    if (!user) { clearInterval(timerRef.current); return; }
    timerRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = SESSION_TIMEOUT_MS - elapsed;
      if (remaining <= 0) {
        clearInterval(timerRef.current);
        localStorage.removeItem('token');
        setUser(null);
        setSessionWarning(false);
        setRemainingSeconds(0);
      } else {
        setRemainingSeconds(Math.ceil(remaining / 1000));
        setSessionWarning(remaining <= WARNING_BEFORE_MS);
      }
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [user]);

  const login = useCallback(async (username, password) => {
    const res = await authApi.login({ username, password });
    localStorage.setItem('token', res.data.token);
    lastActivityRef.current = Date.now();
    await fetchUser();
    return res.data;
  }, [fetchUser]);

  const register = useCallback(async (email, password, name) => {
    const res = await authApi.register({ email, password, name });
    localStorage.setItem('token', res.data.token);
    lastActivityRef.current = Date.now();
    await fetchUser();
    return res.data;
  }, [fetchUser]);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setUser(null);
    setSessionWarning(false);
  }, []);

  const refreshUser = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  const extendSession = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  return (
    <AuthContext.Provider value={{ user, login, register, logout, loading, refreshUser, sessionWarning, remainingSeconds, extendSession }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
